import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SkillsSection from './components/SkillsSection';
import AvailabilitySection from './components/AvailabilitySection';
import WorkHistorySection from './components/WorkHistorySection';
import ReviewsSection from './components/ReviewsSection';
import StatisticsPanel from './components/StatisticsPanel';
import PortfolioGallery from './components/PortfolioGallery';
import { workerProfilesService } from '../../services/supabaseService';
import { reviewsService } from '../../services/reviewsService';

const WorkerProfile = () => {
  const [searchParams] = useSearchParams();
  const workerId = searchParams?.get('workerId');
  
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    if (workerId) {
      fetchWorkerData();
    }
  }, [workerId]);

  const fetchWorkerData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [profileData, reviewsData] = await Promise.all([
        workerProfilesService?.getWorkerProfile(workerId),
        reviewsService?.getUserReviews(workerId)
      ]);

      setProfile(profileData);
      setReviews(reviewsData || []);
      setIsOnline(Math.random() > 0.5); // Simulate online status
    } catch (err) {
      console.error('Error fetching worker data:', err);
      setError(err?.message || 'Failed to load worker profile');
    } finally {
      setLoading(false);
    }
  };

  const handleContactWorker = () => {
    window.location.href = `/messages?userId=${workerId}`;
  };

  const handleHireNow = () => {
    window.location.href = `/post-job?workerId=${workerId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)] space-y-4">
          <Icon name="AlertCircle" className="w-12 h-12 text-error" />
          <p className="text-lg text-muted-foreground">{error || 'Worker profile not found'}</p>
        </div>
      </div>
    );
  }

  const verificationBadges = [];
  if (profile?.isVerified) {
    verificationBadges?.push({ label: 'ID Verified', icon: 'CheckCircle' });
  }
  if (profile?.verificationLevel === 'verified' || profile?.verificationLevel === 'premium') {
    verificationBadges?.push({ label: 'Background Check', icon: 'Shield' });
  }
  if (profile?.verificationLevel === 'premium') {
    verificationBadges?.push({ label: 'Skill Certified', icon: 'Award' });
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{profile?.userProfiles?.fullName || 'Worker Profile'} - DISKARTE</title>
      </Helmet>
      
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl mt-16">
        {/* Profile Header Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Avatar & Online Status */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center">
                  {profile?.userProfiles?.avatarUrl ? (
                    <img 
                      src={profile?.userProfiles?.avatarUrl} 
                      alt={`${profile?.userProfiles?.fullName} profile photo`}
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" className="w-16 h-16 text-primary" />
                  )}
                </div>
                {/* Online Status Indicator */}
                <div className={`absolute bottom-2 right-2 w-6 h-6 rounded-full border-4 border-card ${
                  isOnline ? 'bg-success' : 'bg-muted-foreground'
                }`} title={isOnline ? 'Online' : 'Offline'} />
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    {profile?.userProfiles?.fullName || 'Worker'}
                  </h1>
                  <p className="text-lg text-muted-foreground mb-3">
                    {profile?.bio || 'Professional Service Provider'}
                  </p>
                  
                  {/* Rating & Hourly Rate */}
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5]?.map((star) => (
                          <Icon
                            key={star}
                            name="Star"
                            className={`w-5 h-5 ${
                              star <= Math.round(profile?.rating || 0)
                                ? 'text-warning fill-warning' :'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-lg font-semibold text-foreground">
                        {profile?.rating?.toFixed(1) || '0.0'}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({profile?.totalReviews || 0} reviews)
                      </span>
                    </div>
                    
                    <div className="h-6 w-px bg-border" />
                    
                    <div className="flex items-center gap-2">
                      <Icon name="DollarSign" className="w-5 h-5 text-success" />
                      <span className="text-lg font-bold text-foreground">
                        ₱{profile?.hourlyRateMin || 0} - ₱{profile?.hourlyRateMax || 0}
                      </span>
                      <span className="text-sm text-muted-foreground">/hour</span>
                    </div>
                  </div>

                  {/* Verification Badges */}
                  {verificationBadges?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {verificationBadges?.map((badge, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-1 bg-success/10 text-success px-3 py-1 rounded-full text-sm font-medium"
                        >
                          <Icon name={badge?.icon} className="w-4 h-4" />
                          <span>{badge?.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-4 md:mt-0">
                  <Button
                    onClick={handleContactWorker}
                    variant="default"
                    size="lg"
                    iconName="MessageSquare"
                    className="w-full md:w-auto"
                  >
                    Contact Worker
                  </Button>
                  <Button
                    onClick={handleHireNow}
                    variant="success"
                    size="lg"
                    iconName="Briefcase"
                    className="w-full md:w-auto"
                  >
                    Hire Now
                  </Button>
                </div>
              </div>

              {/* Availability Status */}
              <div className="flex items-center gap-2 p-3 bg-background rounded-lg">
                <Icon 
                  name={isOnline ? 'Wifi' : 'WifiOff'} 
                  className={`w-5 h-5 ${isOnline ? 'text-success' : 'text-muted-foreground'}`} 
                />
                <span className="text-sm font-medium text-foreground">
                  {isOnline ? 'Available now - Typically responds within 1 hour' : 'Currently offline - Will respond soon'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Skills Section */}
            <SkillsSection skills={profile?.skills || []} />

            {/* Work History Section */}
            <WorkHistorySection 
              workerId={workerId}
              totalJobsCompleted={profile?.totalJobsCompleted || 0}
            />

            {/* Portfolio Gallery */}
            <PortfolioGallery workerId={workerId} />

            {/* Reviews Section */}
            <ReviewsSection 
              reviews={reviews}
              workerId={workerId}
              onReviewUpdated={fetchWorkerData}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Statistics Panel */}
            <StatisticsPanel 
              totalJobsCompleted={profile?.totalJobsCompleted || 0}
              totalReviews={profile?.totalReviews || 0}
              rating={profile?.rating || 0}
              experienceYears={profile?.experienceYears || 0}
            />

            {/* Availability Section */}
            <AvailabilitySection availability={profile?.availability || 'Not specified'} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerProfile;